// PositionResolutionService.js
// Service to resolve position TEI from staff TEI and vice versa

export class PositionResolutionService {
  constructor(engine) {
    this.engine = engine;
    this.POSITION_TEI_TYPE = 'UGGo0Ebjl0E';
    this.STAFF_TEI_TYPE = 'OaAbpIs1cV7';
    this.POSITION_ATTRIBUTES = {
      CURRENT_INCUMBENT: 'V66IFsw5kq6'
    };
  }

  /**
   * Get position TEI for a given staff TEI
   * @param {string} staffTeiId - Staff TEI ID
   * @returns {Promise<string|null>} Position TEI ID or null if not found
   */
  async getPositionTeiForStaff(staffTeiId) {
    try {
      console.log('🔍 Resolving position TEI for staff:', staffTeiId);

      // Query positions where current incumbent matches the staff TEI
      const response = await this.engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: this.POSITION_TEI_TYPE,
            filter: [`${this.POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${staffTeiId}`],
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            pageSize: 10 // Should only be one active position per staff
          }
        }
      });

      const positions = response.positions?.trackedEntityInstances || [];

      if (positions.length === 0) {
        console.warn('⚠️ No position found for staff:', staffTeiId);
        return null;
      }

      if (positions.length > 1) {
        console.warn('⚠️ Multiple positions found for staff:', staffTeiId, positions);
        // Return the first one (most recent)
      }

      const positionTei = positions[0].trackedEntityInstance;
      console.log('✅ Found position TEI for staff:', staffTeiId, '->', positionTei);
      return positionTei;

    } catch (error) {
      console.error('❌ Failed to resolve position TEI for staff:', error);
      throw error;
    }
  }

  /**
   * Get staff TEI for a given position TEI
   * @param {string} positionTeiId - Position TEI ID
   * @returns {Promise<string|null>} Staff TEI ID or null if position is vacant
   */
  async getStaffTeiForPosition(positionTeiId) {
    try {
      console.log('🔍 Resolving staff TEI for position:', positionTeiId);

      // Get position details to find current incumbent
      const response = await this.engine.query({
        position: {
          resource: `trackedEntityInstances/${positionTeiId}`,
          params: {
            fields: 'attributes[attribute,value]'
          }
        }
      });

      const position = response.position;
      if (!position) {
        console.warn('⚠️ Position not found:', positionTeiId);
        return null;
      }

      // Extract current incumbent from attributes
      const currentIncumbentAttr = position.attributes?.find(
        attr => attr.attribute === this.POSITION_ATTRIBUTES.CURRENT_INCUMBENT
      );

      const staffTei = currentIncumbentAttr?.value || null;

      if (!staffTei) {
        console.log('ℹ️ Position is vacant:', positionTeiId);
      } else {
        console.log('✅ Found staff TEI for position:', positionTeiId, '->', staffTei);
      }

      return staffTei;

    } catch (error) {
      console.error('❌ Failed to resolve staff TEI for position:', error);
      throw error;
    }
  }

  /**
   * Validate that a position TEI exists and is valid
   * @param {string} positionTeiId - Position TEI ID to validate
   * @returns {Promise<boolean>} True if position exists and is valid
   */
  async validatePositionTei(positionTeiId) {
    try {
      const response = await this.engine.query({
        position: {
          resource: `trackedEntityInstances/${positionTeiId}`,
          params: {
            fields: 'trackedEntityType'
          }
        }
      });

      const isValid = response.position?.trackedEntityType === this.POSITION_TEI_TYPE;
      console.log('🔍 Position TEI validation:', positionTeiId, isValid ? 'valid' : 'invalid');
      return isValid;

    } catch (error) {
      console.error('❌ Position TEI validation failed:', error);
      return false;
    }
  }

  /**
   * Get position details including title and department
   * @param {string} positionTeiId - Position TEI ID
   * @returns {Promise<Object|null>} Position details or null
   */
  async getPositionDetails(positionTeiId) {
    try {
      const response = await this.engine.query({
        position: {
          resource: `trackedEntityInstances/${positionTeiId}`,
          params: {
            fields: 'trackedEntityInstance,attributes[attribute,value]'
          }
        }
      });

      const position = response.position;
      if (!position) return null;

      // Extract position details from attributes
      const attributes = {};
      position.attributes?.forEach(attr => {
        attributes[attr.attribute] = attr.value;
      });

      return {
        id: position.trackedEntityInstance,
        positionId: attributes['VwhhrV1ODQK'], // POSITION_ID
        title: attributes['NWe61Sa41CS'], // POSITION_TITLE
        department: attributes['YsGPrrVLAuM'], // DEPARTMENT
        jobGrade: attributes['gp4AnggIDi8'], // JOB_GRADE
        status: attributes['Ch7TyL91heU'], // POSITION_STATUS
        currentIncumbent: attributes['V66IFsw5kq6'] // CURRENT_INCUMBENT
      };

    } catch (error) {
      console.error('❌ Failed to get position details:', error);
      return null;
    }
  }
}